<script setup>
import { ref } from 'vue'
const pA = "M25.639,218.34l0,-21.343c76.237,0 138.132,-61.895 138.132,-138.132l21.517,0c10.337,1.241 18.563,9.389 19.923,19.689c-0.865,7.964 -2.248,15.773 -4.116,23.393c-15.958,65.113 -67.258,116.414 -132.371,132.372c-7.357,1.803 -14.889,3.155 -22.567,4.025l-0.127,-0c-10.63,-1.071 -19.122,-9.433 -20.391,-20.004Z"
const pB = "M163.107,327.902c15.959,-65.111 67.259,-116.41 132.371,-132.368c7.509,-1.84 15.203,-3.211 23.046,-4.078c10.464,1.218 18.782,9.513 20.037,19.966l0,21.435c-76.237,0 -138.132,61.895 -138.132,138.132l-21.17,0c-10.571,-1.269 -18.934,-9.761 -20.004,-20.391l-0,-1.604c0.872,-7.168 2.164,-14.208 3.852,-21.093Z"

// Eclipse preview — remount SVG to restart SMIL animations
const eclipseKey = ref(0)
const replayEclipse = () => { eclipseKey.value++ }
</script>

<template>
    <div class="min-h-screen bg-[#08080f] p-10">
        <h1 class="text-white text-2xl font-bold mb-1">Logo Animations</h1>
        <p class="text-gray-500 text-sm mb-10">10 variant animace loga Roori + Eclipse login animace</p>

        <!-- Login animation preview -->
        <div class="mb-10 p-8 bg-white/5 rounded-2xl flex flex-col items-center gap-6">
            <div class="text-white text-sm font-semibold">Login Animace — Split 45° + Shimmer</div>
            <div class="bg-[#F8F8F8] rounded-2xl flex items-center justify-center" style="width: 280px; height: 220px">
                <svg :key="eclipseKey" viewBox="0 0 364 422" width="160" xmlns="http://www.w3.org/2000/svg">
                    <defs>
                        <linearGradient id="prev-shimmer" x1="-350" y1="-350" x2="50" y2="50" gradientUnits="userSpaceOnUse">
                            <stop offset="0%"   stop-color="#003cff" />
                            <stop offset="38%"  stop-color="#003cff" />
                            <stop offset="50%"  stop-color="#93b4ff" />
                            <stop offset="62%"  stop-color="#003cff" />
                            <stop offset="100%" stop-color="#003cff" />
                            <animate attributeName="x1" from="-350" to="350" begin="0.65s" dur="1.6s" fill="freeze" />
                            <animate attributeName="y1" from="-350" to="350" begin="0.65s" dur="1.6s" fill="freeze" />
                            <animate attributeName="x2" from="50"   to="750" begin="0.65s" dur="1.6s" fill="freeze" />
                            <animate attributeName="y2" from="50"   to="750" begin="0.65s" dur="1.6s" fill="freeze" />
                        </linearGradient>
                    </defs>
                    <path :d="pA" fill="url(#prev-shimmer)" class="prev-pa"/>
                    <path :d="pB" fill="url(#prev-shimmer)" class="prev-pb"/>
                </svg>
            </div>
            <button class="text-xs text-blue-400 hover:text-blue-300 border border-blue-400/30 hover:border-blue-400/60 px-4 py-1.5 rounded-lg transition-colors" @click="replayEclipse">
                Přehrát znovu
            </button>
        </div>

        <div class="grid grid-cols-5 gap-4">

            <!-- 1. Path Draw -->
            <div class="flex flex-col items-center gap-4 p-6 bg-white/5 rounded-2xl">
                <div class="h-28 flex items-center justify-center">
                    <svg viewBox="0 0 364 422" width="80" xmlns="http://www.w3.org/2000/svg">
                        <path :d="pA" fill="none" stroke="#003cff" stroke-width="20" class="draw-path" style="animation-delay: 0s" />
                        <path :d="pB" fill="none" stroke="#003cff" stroke-width="20" class="draw-path" style="animation-delay: 0.7s" />
                    </svg>
                </div>
                <div class="text-center">
                    <div class="text-white text-xs font-semibold">1. Path Draw</div>
                    <div class="text-gray-500 text-xs mt-0.5">Stroke kreslí tvar od nuly</div>
                </div>
            </div>

            <!-- 2. Metallic Shimmer -->
            <div class="flex flex-col items-center gap-4 p-6 bg-white/5 rounded-2xl">
                <div class="h-28 flex items-center justify-center">
                    <svg viewBox="0 0 364 422" width="80" xmlns="http://www.w3.org/2000/svg">
                        <defs>
                            <linearGradient id="shimmer-g" x1="-364" y1="0" x2="0" y2="0" gradientUnits="userSpaceOnUse">
                                <stop offset="0%" stop-color="#003cff" />
                                <stop offset="35%" stop-color="#003cff" />
                                <stop offset="50%" stop-color="#7ba8ff" />
                                <stop offset="65%" stop-color="#003cff" />
                                <stop offset="100%" stop-color="#003cff" />
                                <animate attributeName="x1" from="-364" to="364" dur="2.5s" repeatCount="indefinite" />
                                <animate attributeName="x2" from="0" to="728" dur="2.5s" repeatCount="indefinite" />
                            </linearGradient>
                        </defs>
                        <path :d="pA" fill="url(#shimmer-g)" />
                        <path :d="pB" fill="url(#shimmer-g)" />
                    </svg>
                </div>
                <div class="text-center">
                    <div class="text-white text-xs font-semibold">2. Metallic Shimmer</div>
                    <div class="text-gray-500 text-xs mt-0.5">Světelný lesk přejíždí přes logo</div>
                </div>
            </div>

            <!-- 3. Neon Glow -->
            <div class="flex flex-col items-center gap-4 p-6 bg-white/5 rounded-2xl">
                <div class="h-28 flex items-center justify-center">
                    <svg viewBox="0 0 364 422" width="80" class="anim-neon" xmlns="http://www.w3.org/2000/svg">
                        <path :d="pA" fill="#003cff" />
                        <path :d="pB" fill="#003cff" />
                    </svg>
                </div>
                <div class="text-center">
                    <div class="text-white text-xs font-semibold">3. Neon Glow</div>
                    <div class="text-gray-500 text-xs mt-0.5">Elektrické neonové záření</div>
                </div>
            </div>

            <!-- 4. 3D Flip -->
            <div class="flex flex-col items-center gap-4 p-6 bg-white/5 rounded-2xl">
                <div class="h-28 flex items-center justify-center" style="perspective: 500px">
                    <svg viewBox="0 0 364 422" width="80" class="anim-flip" xmlns="http://www.w3.org/2000/svg">
                        <path :d="pA" fill="#003cff" />
                        <path :d="pB" fill="#003cff" />
                    </svg>
                </div>
                <div class="text-center">
                    <div class="text-white text-xs font-semibold">4. 3D Flip</div>
                    <div class="text-gray-500 text-xs mt-0.5">Rotuje jako mince v prostoru</div>
                </div>
            </div>

            <!-- 5. Liquid Fill -->
            <div class="flex flex-col items-center gap-4 p-6 bg-white/5 rounded-2xl">
                <div class="h-28 flex items-center justify-center">
                    <svg viewBox="0 0 364 422" width="80" xmlns="http://www.w3.org/2000/svg">
                        <defs>
                            <clipPath id="liquid-clip">
                                <rect x="0" y="0" width="364" height="422">
                                    <animate attributeName="y" from="422" to="0" dur="2.5s" repeatCount="indefinite" calcMode="spline" keySplines="0.42 0 0.58 1" keyTimes="0;1" />
                                    <animate attributeName="height" from="422" to="422" dur="2.5s" repeatCount="indefinite" />
                                </rect>
                            </clipPath>
                        </defs>
                        <path :d="pA" fill="#1e2d4a" />
                        <path :d="pB" fill="#1e2d4a" />
                        <g clip-path="url(#liquid-clip)">
                            <path :d="pA" fill="#003cff" />
                            <path :d="pB" fill="#003cff" />
                        </g>
                    </svg>
                </div>
                <div class="text-center">
                    <div class="text-white text-xs font-semibold">5. Liquid Fill</div>
                    <div class="text-gray-500 text-xs mt-0.5">Plní se jako kapalina zdola</div>
                </div>
            </div>

            <!-- 6. Split Merge -->
            <div class="flex flex-col items-center gap-4 p-6 bg-white/5 rounded-2xl">
                <div class="h-28 flex items-center justify-center overflow-hidden" style="width: 120px">
                    <svg viewBox="0 0 364 422" width="80" xmlns="http://www.w3.org/2000/svg">
                        <g class="split-left">
                            <path :d="pA" fill="#003cff" />
                        </g>
                        <g class="split-right">
                            <path :d="pB" fill="#003cff" />
                        </g>
                    </svg>
                </div>
                <div class="text-center">
                    <div class="text-white text-xs font-semibold">6. Split Merge</div>
                    <div class="text-gray-500 text-xs mt-0.5">Části přilétají z protisměru</div>
                </div>
            </div>

            <!-- 7. Heartbeat -->
            <div class="flex flex-col items-center gap-4 p-6 bg-white/5 rounded-2xl">
                <div class="h-28 flex items-center justify-center">
                    <svg viewBox="0 0 364 422" width="80" class="anim-heartbeat" xmlns="http://www.w3.org/2000/svg">
                        <path :d="pA" fill="#003cff" />
                        <path :d="pB" fill="#003cff" />
                    </svg>
                </div>
                <div class="text-center">
                    <div class="text-white text-xs font-semibold">7. Heartbeat</div>
                    <div class="text-gray-500 text-xs mt-0.5">Pulzuje rytmus srdce</div>
                </div>
            </div>

            <!-- 8. Chroma Glitch -->
            <div class="flex flex-col items-center gap-4 p-6 bg-white/5 rounded-2xl">
                <div class="h-28 flex items-center justify-center">
                    <div class="relative" style="width: 80px; height: 93px">
                        <svg viewBox="0 0 364 422" width="80" height="93" class="absolute inset-0 anim-glitch-r" xmlns="http://www.w3.org/2000/svg">
                            <path :d="pA" fill="#ff2d55" />
                            <path :d="pB" fill="#ff2d55" />
                        </svg>
                        <svg viewBox="0 0 364 422" width="80" height="93" class="absolute inset-0 anim-glitch-b" xmlns="http://www.w3.org/2000/svg">
                            <path :d="pA" fill="#00e5ff" />
                            <path :d="pB" fill="#00e5ff" />
                        </svg>
                        <svg viewBox="0 0 364 422" width="80" height="93" class="absolute inset-0" xmlns="http://www.w3.org/2000/svg">
                            <path :d="pA" fill="#003cff" />
                            <path :d="pB" fill="#003cff" />
                        </svg>
                    </div>
                </div>
                <div class="text-center">
                    <div class="text-white text-xs font-semibold">8. Chroma Glitch</div>
                    <div class="text-gray-500 text-xs mt-0.5">RGB rozpad digitální chyba</div>
                </div>
            </div>

            <!-- 9. Floating Shadow -->
            <div class="flex flex-col items-center gap-4 p-6 bg-white/5 rounded-2xl">
                <div class="h-28 flex items-center justify-center">
                    <svg viewBox="0 0 364 422" width="80" class="anim-float" xmlns="http://www.w3.org/2000/svg">
                        <path :d="pA" fill="#003cff" />
                        <path :d="pB" fill="#003cff" />
                    </svg>
                </div>
                <div class="text-center">
                    <div class="text-white text-xs font-semibold">9. Floating</div>
                    <div class="text-gray-500 text-xs mt-0.5">Levituje se stínem níže</div>
                </div>
            </div>

            <!-- 10. Elastic Pop -->
            <div class="flex flex-col items-center gap-4 p-6 bg-white/5 rounded-2xl">
                <div class="h-28 flex items-center justify-center">
                    <svg viewBox="0 0 364 422" width="80" class="anim-elastic" xmlns="http://www.w3.org/2000/svg">
                        <path :d="pA" fill="#003cff" />
                        <path :d="pB" fill="#003cff" />
                    </svg>
                </div>
                <div class="text-center">
                    <div class="text-white text-xs font-semibold">10. Elastic Pop</div>
                    <div class="text-gray-500 text-xs mt-0.5">Vyskočí jako gumička</div>
                </div>
            </div>

        </div>
    </div>
</template>

<style scoped>
/* ─── 1. Path Draw ─────────────────────────────────────────────── */
.draw-path {
    stroke-dasharray: 2000;
    stroke-dashoffset: 2000;
    animation: path-draw 3s ease-in-out infinite;
}

@keyframes path-draw {
    0%   { stroke-dashoffset: 2000; opacity: 1; }
    50%  { stroke-dashoffset: 0;    opacity: 1; }
    80%  { stroke-dashoffset: 0;    opacity: 0.9; }
    100% { stroke-dashoffset: -2000; opacity: 0; }
}

/* ─── 3. Neon Glow ─────────────────────────────────────────────── */
.anim-neon {
    animation: neon-pulse 2s ease-in-out infinite;
}

@keyframes neon-pulse {
    0%, 100% {
        filter:
            drop-shadow(0 0 2px #003cff)
            drop-shadow(0 0 6px rgba(0,60,255,0.5));
    }
    50% {
        filter:
            drop-shadow(0 0 8px #003cff)
            drop-shadow(0 0 20px #003cff)
            drop-shadow(0 0 50px rgba(0,60,255,0.7));
    }
}

/* ─── 4. 3D Flip ───────────────────────────────────────────────── */
.anim-flip {
    animation: flip-3d 3.5s ease-in-out infinite;
    transform-style: preserve-3d;
}

@keyframes flip-3d {
    0%        { transform: rotateY(0deg); }
    40%       { transform: rotateY(180deg); }
    55%       { transform: rotateY(180deg); }
    95%, 100% { transform: rotateY(360deg); }
}

/* ─── 6. Split Merge ───────────────────────────────────────────── */
.split-left {
    animation: split-from-left 2.8s ease-in-out infinite;
}
.split-right {
    animation: split-from-right 2.8s ease-in-out infinite;
}

@keyframes split-from-left {
    0%, 100% { transform: translateX(-130px); opacity: 0; }
    25%, 70%  { transform: translateX(0);      opacity: 1; }
}
@keyframes split-from-right {
    0%, 100% { transform: translateX(130px); opacity: 0; }
    25%, 70%  { transform: translateX(0);     opacity: 1; }
}

/* ─── 7. Heartbeat ─────────────────────────────────────────────── */
.anim-heartbeat {
    animation: heartbeat 1.6s ease-in-out infinite;
    transform-origin: center;
}

@keyframes heartbeat {
    0%   { transform: scale(1); }
    12%  { transform: scale(1.18); }
    24%  { transform: scale(0.96); }
    36%  { transform: scale(1.12); }
    50%  { transform: scale(1); }
    100% { transform: scale(1); }
}

/* ─── 8. Chroma Glitch ─────────────────────────────────────────── */
.anim-glitch-r {
    animation: glitch-red 4s step-end infinite;
    opacity: 0;
}
.anim-glitch-b {
    animation: glitch-blue 4s step-end infinite;
    opacity: 0;
}

@keyframes glitch-red {
    0%, 88%, 100% { opacity: 0; transform: translate(0, 0); }
    89%  { opacity: 1; transform: translate(-5px, 1px);  clip-path: inset(8%  0 65% 0); }
    90%  { opacity: 1; transform: translate(4px, -1px);  clip-path: inset(55% 0 15% 0); }
    91%  { opacity: 1; transform: translate(-3px, 2px);  clip-path: inset(30% 0 45% 0); }
    92%  { opacity: 1; transform: translate(6px, 0px);   clip-path: inset(72% 0 5%  0); }
    93%  { opacity: 0; transform: translate(0, 0); }
}

@keyframes glitch-blue {
    0%, 90%, 100% { opacity: 0; transform: translate(0, 0); }
    91%  { opacity: 1; transform: translate(5px, -2px);  clip-path: inset(3%  0 80% 0); }
    92%  { opacity: 1; transform: translate(-4px, 1px);  clip-path: inset(48% 0 22% 0); }
    93%  { opacity: 1; transform: translate(3px, -1px);  clip-path: inset(78% 0 3%  0); }
    94%  { opacity: 1; transform: translate(-6px, 2px);  clip-path: inset(20% 0 60% 0); }
    95%  { opacity: 0; transform: translate(0, 0); }
}

/* ─── 9. Floating ──────────────────────────────────────────────── */
.anim-float {
    animation: float-hover 3s ease-in-out infinite;
    transform-origin: center bottom;
}

@keyframes float-hover {
    0%, 100% {
        transform: translateY(0px);
        filter: drop-shadow(0 12px 20px rgba(0, 60, 255, 0.5));
    }
    50% {
        transform: translateY(-14px);
        filter: drop-shadow(0 26px 32px rgba(0, 60, 255, 0.2));
    }
}

/* ─── Login preview: Split 45° + Shimmer ──────────────────────── */
.prev-pa {
    transform-box: fill-box;
    transform-origin: center;
    animation: prev-in-a 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) 0s both;
}
.prev-pb {
    transform-box: fill-box;
    transform-origin: center;
    animation: prev-in-b 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) 0.08s both;
}

@keyframes prev-in-a {
    from { transform: translate(-140px, -110px); opacity: 0; }
    to   { transform: translate(0, 0);           opacity: 1; }
}
@keyframes prev-in-b {
    from { transform: translate(140px, 110px); opacity: 0; }
    to   { transform: translate(0, 0);         opacity: 1; }
}

/* ─── 10. Elastic Pop ──────────────────────────────────────────── */
.anim-elastic {
    animation: elastic-pop 3s ease-out infinite;
    transform-origin: center;
}

@keyframes elastic-pop {
    0%    { transform: scale(0);    opacity: 0; }
    15%   { transform: scale(1.28); opacity: 1; }
    28%   { transform: scale(0.88); }
    38%   { transform: scale(1.10); }
    48%   { transform: scale(0.96); }
    56%   { transform: scale(1.03); }
    62%   { transform: scale(1);    opacity: 1; }
    75%   { transform: scale(1);    opacity: 1; }
    90%   { transform: scale(0);    opacity: 0; }
    100%  { transform: scale(0);    opacity: 0; }
}
</style>
